import { useState, useRef, useCallback } from 'react';
import * as PIXI from 'pixi.js';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { HouseFurniture, FurnitureCatalog } from '../../models/apiTypes';
import {
  buyFurnitureBulkInteriorObjectsInteriorObjectIdFurnitureBuyBulkPost,
  buyFurnitureInteriorObjectsInteriorObjectIdFurnitureBuyPost,
  type FurnitureBuyResponse,
} from '../../client';
import { useCreateUserAction } from '../../hooks/useCreateUserAction';
import useFurniturePreviewStore from '../../stores/useFurniturePreviewStore';
import { showAlert } from '../../utils/telegram/alert';
import { isInvoiceLinkResponse } from './furnitureApi';
import { stageLayout, type LayoutPatch } from './state/layoutSync';
import { collectCarriedItems } from './interaction/surfaceCarry';
import { recomputeAllZIndexes, getFurnitureDepthComponents } from '../../domain/depthMath';
import { getTileDims, isWallMountedItem } from '../../domain/tileMath';
import { drawAdminTileOverlayImperative } from './render/overlays';
import type { RadialMenuActionKey } from './interaction/radialMenu';
import type { RenderFurniture } from '../../domain/types';
import type { RoomSceneState } from './state/roomSceneState';
import { asFurnitureMarker, asFurnitureSprite, liveFurnitureData } from './render/pixiExtensions';
import { WINDOW_VERTICAL_OFFSET } from '../../domain/windowSnap';

// ── Action cluster: mirror / unplace / radial dispatch / preview purchase ──
// Extracted verbatim from RoomCanvas (decomposition step: action cluster).
// Bodies are byte-identical to the originals except that RoomCanvas-owned refs,
// admin-tile setters and PIXI-lifecycle callbacks are received as parameters.
// The purchase-modal state (previewPurchaseFurniture / InstanceId / Loading)
// and the TDZ-breaking handlePreviewBuyAllRef moved here with the cluster.

// Structurally identical to the local `AdminTileMode` inside RoomCanvas.
export type AdminTileMode = 'footprint' | 'surface' | null;

export interface UseRoomActionsParams {
  mapObjectId: number;
  isAdmin: boolean;
  closeRadialMenu: () => void;
  sceneRef: React.MutableRefObject<RoomSceneState>;
  setAdminTileCatalogId: (id: number | null) => void;
  setAdminTileMode: (mode: AdminTileMode) => void;
  setAdminSelectedTiles: (tiles: Set<string>) => void;
  logFurnitureDiagnostics: () => void;
}

export interface UseRoomActionsResult {
  previewPurchaseFurniture: FurnitureCatalog | null;
  setPreviewPurchaseFurniture: React.Dispatch<React.SetStateAction<FurnitureCatalog | null>>;
  previewPurchaseInstanceId: number | null;
  setPreviewPurchaseInstanceId: React.Dispatch<React.SetStateAction<number | null>>;
  previewPurchaseLoading: 'pearls' | 'stars' | null;
  setPreviewPurchaseLoading: React.Dispatch<React.SetStateAction<'pearls' | 'stars' | null>>;
  handleMirrorToggle: (furniture: RenderFurniture) => Promise<void>;
  handleUnplaceFurniture: (furniture: RenderFurniture) => Promise<void>;
  executeRadialAction: (actionKey: RadialMenuActionKey, furniture?: RenderFurniture | null) => void;
  refreshFurnitureCache: () => Promise<void>;
  handlePreviewBuy: () => Promise<void>;
  handlePreviewBuyAll: () => Promise<void>;
}

export function useRoomActions({
  mapObjectId,
  isAdmin,
  closeRadialMenu,
  sceneRef,
  setAdminTileCatalogId,
  setAdminTileMode,
  setAdminSelectedTiles,
  logFurnitureDiagnostics,
}: UseRoomActionsParams): UseRoomActionsResult {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { createUserAction } = useCreateUserAction();
  const {
    previewItems,
    updatePreviewPosition,
    togglePreviewMirror,
    removePreviewItem,
    clearPreviewFurniture,
  } = useFurniturePreviewStore();

  const [previewPurchaseFurniture, setPreviewPurchaseFurniture] = useState<FurnitureCatalog | null>(null);
  const [previewPurchaseInstanceId, setPreviewPurchaseInstanceId] = useState<number | null>(null);
  const [previewPurchaseLoading, setPreviewPurchaseLoading] = useState<'pearls' | 'stars' | null>(null);

  // Ref to break TDZ: handlePreviewBuyAll is defined after executeRadialAction
  const handlePreviewBuyAllRef = useRef<() => Promise<void>>(async () => {});

  // Mirror toggle handler
  const handleMirrorToggle = useCallback(async (furniture: RenderFurniture) => {
    const currentFurniture = furniture.is_preview
      ? null
      : queryClient.getQueryData<HouseFurniture[]>(['houseFurniture', mapObjectId])?.find((f) => f.id === furniture.id);
    const newMirrored = !(currentFurniture?.is_mirrored ?? furniture.is_mirrored);

    // Collect all items transitively placed on root's surface (shared fixed-point
    // discovery in room/surfaceCarry.ts). We keep the marker so we can reposition
    // items after mirroring.
    const toMoveItems: Array<{ marker: PIXI.Container; fData: RenderFurniture }> = [];
    const rootMarker = sceneRef.current.furniture.markers.get(furniture.id);
    if (rootMarker) {
      const { carriedItems: mirrorCarried } = collectCarriedItems(sceneRef.current.furniture.markers, furniture.id, rootMarker);
      // Domain returns SceneNode; markers are PIXI.Container at runtime.
      for (const c of mirrorCarried) toMoveItems.push({ marker: c.marker as PIXI.Container, fData: c.furnitureData });
    }

    // The root and everything standing on it change in ONE local layout write
    // (one undo step), synced to the backend in the background — see layoutSync.
    const patches: LayoutPatch[] = [];

    // Mirror the root item.
    if (furniture.is_preview) {
      togglePreviewMirror(-(furniture.id + 100000));
    } else {
      const traceId = `mirror-${furniture.id}-${Date.now()}-${++sceneRef.current.mirrorTrace.seq}`;
      sceneRef.current.mirrorTrace.map.set(furniture.id, {
        traceId,
        startedAt: Date.now(),
        targetMirror: newMirrored,
      });
      patches.push({ id: furniture.id, is_mirrored: newMirrored });
    }

    // Reposition all surface items after the root is mirrored.
    // When a furniture item is mirrored horizontally, its surface tile positions reflect
    // through its world X axis. In isometric space, horizontal screen mirror swaps tile
    // axes (dx,dy) → (dy,dx), which equals a reflection: newX = 2*rootX - itemX, newY unchanged.
    // This applies recursively: reflecting all items through the root's X correctly repositions
    // every level of the stack (a nightstand on the table, a cup on the nightstand, etc.).
    if (rootMarker && toMoveItems.length > 0) {
      const rootX = rootMarker.position.x;
      for (const { marker: cMarker, fData: cfData } of toMoveItems) {
        const newX = 2 * rootX - cMarker.position.x;
        const newY = cMarker.position.y; // Y is unchanged by horizontal mirror
        cMarker.position.set(newX, newY);
        if (cfData.is_preview) {
          updatePreviewPosition(-(cfData.id + 100000), newX, newY);
        } else {
          patches.push({ id: cfData.id, x: newX, y: newY });
        }
      }
      recomputeAllZIndexes(sceneRef.current.furniture.markers, sceneRef.current.drag.state, sceneRef.current.drag.draggingCarriedIds, logFurnitureDiagnostics);
    }
    stageLayout(queryClient, mapObjectId, patches);
    // logFurnitureDiagnostics — нова identity коли t/i18n змінюється; mirror не залежить від dump.
    // sceneRef стабільний.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- diagnostics optional; sceneRef stable add
  }, [mapObjectId, queryClient, t, togglePreviewMirror, updatePreviewPosition, sceneRef]);

  // Unplace furniture handler
  const handleUnplaceFurniture = useCallback(async (furniture: RenderFurniture) => {
    if (furniture.is_preview) {
      removePreviewItem(-(furniture.id + 100000));
      return;
    }
    stageLayout(queryClient, mapObjectId, [{ id: furniture.id, is_placed: false }]);
  }, [mapObjectId, queryClient, removePreviewItem]);

  const executeRadialAction = useCallback((actionKey: RadialMenuActionKey, furniture?: RenderFurniture | null) => {
    if (!furniture) return; // remaining actions require a furniture target

    // Named handlers: preconditions (is_preview, isAdmin) stay inside handlers —
    // never part of the action key — so Record exhaustiveness stays intact.
    const handleMirror = (f: RenderFurniture) => {
      void handleMirrorToggle(f);
    };

    const handleUnplace = (f: RenderFurniture) => {
      void handleUnplaceFurniture(f);
    };

    const handleBuy = (f: RenderFurniture) => {
      if (!f.is_preview) return;
      setPreviewPurchaseFurniture(f.furniture_catalog);
      setPreviewPurchaseInstanceId(-(f.id + 100000));
    };

    const handleBuyAll = (f: RenderFurniture) => {
      if (!f.is_preview) return;
      void handlePreviewBuyAllRef.current();
    };

    const handleCancel = (f: RenderFurniture) => {
      if (!f.is_preview) return;
      removePreviewItem(-(f.id + 100000));
      closeRadialMenu();
    };

    const handleEndTestdrive = (f: RenderFurniture) => {
      if (!f.is_preview) return;
      removePreviewItem(-(f.id + 100000));
      closeRadialMenu();
    };

    // Shared helper for wall_height_up / wall_height_down (separate map entries).
    const handleWallHeightChange = (f: RenderFurniture, direction: 'up' | 'down') => {
      const STEP = 25; // px per click
      // wall_floor_offset is per-instance: each placed window/painting has its own height.
      const marker = asFurnitureMarker(sceneRef.current.furniture.markers.get(f.id));
      const sp = asFurnitureSprite(marker?.children[0] as PIXI.Sprite | undefined);
      const liveFData = liveFurnitureData(sp, marker);
      const currentOffset = liveFData?.wall_floor_offset ?? f.wall_floor_offset ?? 0;
      const newOffset = direction === 'up' ? currentOffset - STEP : currentOffset + STEP;
      // Update only THIS marker's furnitureData and position.
      if (marker) {
        if (sp?.furnitureData) {
          sp.furnitureData = { ...sp.furnitureData, wall_floor_offset: newOffset };
        }
        if (marker.furnitureData) {
          marker.furnitureData = { ...marker.furnitureData, wall_floor_offset: newOffset };
        }
        const fData = (liveFData ?? f) as RenderFurniture;
        if (isWallMountedItem(fData.furniture_catalog) && fData.y != null && fData.is_placed) {
          marker.position.y = (fData.y ?? 0) + WINDOW_VERTICAL_OFFSET + newOffset;
        }
      }
      // Per-instance: local cache now, backend in the next layout batch.
      stageLayout(queryClient, mapObjectId, [{ id: f.id, wall_floor_offset: newOffset }]);
      closeRadialMenu();
    };

    // Shared helper for set_footprint / set_surface (separate map entries).
    // isAdmin is a precondition inside the handler, not part of the key.
    const handleSetAdminTiles = (f: RenderFurniture, mode: Exclude<AdminTileMode, null>) => {
      if (!isAdmin) return;
      // Enter admin tile selection mode for this furniture's catalog item.
      // IMPORTANT: `furniture` is captured in a stale closure from setupFurnitureInteraction.
      // After saving tiles the sprite's furnitureData is updated (renderObjects line 1966),
      // but the closure is not. Always read catalog data from the LIVE sprite furnitureData.
      const marker = asFurnitureMarker(sceneRef.current.furniture.markers.get(f.id));
      const sp = asFurnitureSprite(marker?.children[0] as PIXI.Sprite | undefined);
      const liveFData = liveFurnitureData(sp, marker);
      const liveCatalog = liveFData?.furniture_catalog ?? f.furniture_catalog;
      if (marker) {
        const { w: widthTiles, h: heightTiles, isRug: itemIsRug } = getTileDims(liveCatalog);
        const depth = getFurnitureDepthComponents(marker, widthTiles, heightTiles, itemIsRug);
        sceneRef.current.admin.tileCenter = { tileX: Math.round(depth.tileX), tileY: Math.round(depth.tileY) };
      } else {
        sceneRef.current.admin.tileCenter = null;
      }

      setAdminTileCatalogId(liveFData?.furniture_catalog_id ?? f.furniture_catalog_id);
      setAdminTileMode(mode);
      sceneRef.current.admin.tileMode = mode; // sync ref immediately so event handlers see it at once
      // Track whether this item is currently mirrored. Tiles are stored canonically (non-mirrored)
      // in the DB; when the item is mirrored we show the visually-correct (swapped) tiles in the
      // editor and reverse the swap on save so the DB always stores canonical data.
      const isMirroredNow = (sp?.scale?.x ?? 1) < 0;
      sceneRef.current.admin.tileItemIsMirrored = isMirroredNow;
      // Pre-load existing tiles from LIVE catalog data (not stale closure).
      // If footprint_tiles is null in DB, fall back to the heuristic tiles that the
      // frontend is already using visually — so the admin can see and edit them.
      const existingTiles = mode === 'footprint'
        ? liveCatalog.footprint_tiles
        : liveCatalog.surface_tiles;
      const initialTiles = (existingTiles && existingTiles.length > 0)
        ? new Set(existingTiles.map(([dx, dy]) => {
            // Show tiles in the current visual orientation; canonical ↔ visual via (dx,dy)↔(dy,dx)
            const [tdx, tdy] = isMirroredNow ? [dy, dx] : [dx, dy];
            return `${tdx},${tdy}`;
          }))
        : new Set<string>();
      setAdminSelectedTiles(initialTiles);
      sceneRef.current.admin.selectedTiles = initialTiles; // sync ref immediately so drag-mode detection works on first click
      // Immediately draw overlay imperatively — don't wait for useEffect to catch up.
      drawAdminTileOverlayImperative(sceneRef.current.overlays.adminTile, mode, initialTiles, sceneRef.current.admin.tileCenter);
    };

    // Exhaustive dispatch: adding a key to RadialMenuActionKey without a handler here is a compile error.
    const handlers: Record<RadialMenuActionKey, (f: RenderFurniture) => void> = {
      mirror: handleMirror,
      unplace: handleUnplace,
      buy: handleBuy,
      buy_all: handleBuyAll,
      cancel: handleCancel,
      end_testdrive: handleEndTestdrive,
      wall_height_up: (f) => handleWallHeightChange(f, 'up'),
      wall_height_down: (f) => handleWallHeightChange(f, 'down'),
      set_footprint: (f) => handleSetAdminTiles(f, 'footprint'),
      set_surface: (f) => handleSetAdminTiles(f, 'surface'),
    };

    handlers[actionKey](furniture);
  }, [handleMirrorToggle, handleUnplaceFurniture, removePreviewItem, closeRadialMenu, isAdmin, mapObjectId, queryClient, sceneRef, setAdminSelectedTiles, setAdminTileCatalogId, setAdminTileMode]);

  const refreshFurnitureCache = useCallback(async () => {
    // Backend auto-places furniture on purchase; just refresh the local cache.
    await queryClient.invalidateQueries({ queryKey: ["houseFurniture", mapObjectId] });
    await queryClient.refetchQueries({ queryKey: ["houseFurniture", mapObjectId] });
  }, [mapObjectId, queryClient]);

  const handlePreviewBuy = useCallback(async () => {
    if (!previewPurchaseFurniture || previewPurchaseInstanceId === null) return;
    const currency = previewPurchaseFurniture.currency ?? null;
    setPreviewPurchaseLoading((currency as 'pearls' | 'stars' | null) ?? 'pearls');
    // Get preview position to preserve after purchase
    const previewItem = previewItems.find(p => p.instanceId === previewPurchaseInstanceId);
    const buyOptions = previewItem ? {
      x: previewItem.x,
      y: previewItem.y,
      is_mirrored: previewItem.isMirrored,
      placement_mode: previewItem.placement_mode,
    } : undefined;
    const instId = previewPurchaseInstanceId;
    try {
      const { data: result } = await buyFurnitureInteriorObjectsInteriorObjectIdFurnitureBuyPost({
        path: { interior_object_id: mapObjectId },
        body: {
          furniture_catalog_id: previewPurchaseFurniture.id,
          ...buyOptions,
        },
        throwOnError: true,
      });

      if (isInvoiceLinkResponse(result)) {
        // Stars payment — open Telegram invoice
        // Telegram WebApp is injected at runtime; no stable TS types in this project.
        const tg = window.Telegram?.WebApp;
        if (tg?.openInvoice) {
          tg.openInvoice(result.invoice_link, async (status: string) => {
            if (status === 'paid') {
              createUserAction('buy_furniture');
              sceneRef.current.highlight.pendingCatalogId = previewPurchaseFurniture.id;
              await refreshFurnitureCache();
              removePreviewItem(instId);
              setPreviewPurchaseFurniture(null);
              setPreviewPurchaseInstanceId(null);
              closeRadialMenu();
            } else if (status === 'cancelled') {
              showAlert(t('Payment cancelled'));
            } else if (status === 'failed') {
              showAlert(t('Payment failed'));
            }
            setPreviewPurchaseLoading(null);
          });
        } else {
          window.open(result.invoice_link, '_blank');
          setPreviewPurchaseLoading(null);
        }
      } else {
        // Pearls / free payment
        createUserAction('buy_furniture');
        const buyResult = result as FurnitureBuyResponse;
        if (buyResult.remaining_currency !== undefined) {
          queryClient.setQueryData<import('../../models/user').UserData>(["userData"], (old) =>
            old ? { ...old, currency_balance: buyResult.remaining_currency as number } : old
          );
        }
        sceneRef.current.highlight.pendingCatalogId = previewPurchaseFurniture.id;
        await refreshFurnitureCache();
        removePreviewItem(instId);
        setPreviewPurchaseFurniture(null);
        setPreviewPurchaseInstanceId(null);
        closeRadialMenu();
        showAlert(t('Furniture purchased'));
        setPreviewPurchaseLoading(null);
      }
    } catch (err) {
      console.error('[RoomCanvas] Failed to buy preview furniture:', err);
      showAlert(t('Failed to purchase furniture'));
      setPreviewPurchaseLoading(null);
    }
    // createUserAction з useCreateUserAction() — нова fn щорендеру; body викликає fire-and-forget.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- createUserAction unstable each render
  }, [previewPurchaseFurniture, previewPurchaseInstanceId, previewItems, mapObjectId, refreshFurnitureCache, removePreviewItem, closeRadialMenu, queryClient, t]);

  const handlePreviewBuyAll = useCallback(async () => {
    const items = [...previewItems];
    if (items.length === 0) return;
    closeRadialMenu();
    setPreviewPurchaseLoading('pearls');
    try {
      const bulkItems = items.map(item => ({
        furniture_catalog_id: item.furniture.id,
        x: item.x,
        y: item.y,
        is_mirrored: item.isMirrored,
        placement_mode: item.placement_mode,
      }));
      const { data: result } = await buyFurnitureBulkInteriorObjectsInteriorObjectIdFurnitureBuyBulkPost({
        path: { interior_object_id: mapObjectId },
        body: { items: bulkItems },
        throwOnError: true,
      });

      // Update currency from pearl purchases
      if (result.remaining_currency !== undefined) {
        queryClient.setQueryData<import('../../models/user').UserData>(["userData"], (old) =>
          old ? { ...old, currency_balance: result.remaining_currency as number } : old
        );
      }

      // Handle stars invoice if returned
      if (result.invoice_link) {
        // Telegram WebApp is injected at runtime; no stable TS types in this project.
        const tg = window.Telegram?.WebApp;
        if (tg?.openInvoice) {
          tg.openInvoice(result.invoice_link, async (status: string) => {
            if (status === 'paid') {
              createUserAction('buy_furniture');
              clearPreviewFurniture();
              await refreshFurnitureCache();
              showAlert(t('All furniture purchased'));
            } else if (status === 'cancelled') {
              // Pearl items already bought — remove them from preview, keep star items
              const starInstanceIds = new Set(items.filter(i => i.furniture.currency === 'stars').map(i => i.instanceId));
              for (const item of items) {
                if (!starInstanceIds.has(item.instanceId)) removePreviewItem(item.instanceId);
              }
              await refreshFurnitureCache();
              showAlert(t('Payment cancelled'));
            } else if (status === 'failed') {
              showAlert(t('Payment failed'));
            }
            setPreviewPurchaseLoading(null);
          });
          return; // Don't set loading to null — invoice callback will do it
        }
      }

      // No invoice (all pearls) — done
      createUserAction('buy_furniture');
      clearPreviewFurniture();
      await refreshFurnitureCache();
      showAlert(t('All furniture purchased'));
    } catch (err: unknown) {
      console.error('[RoomCanvas] Failed to buy all preview furniture:', err);
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('Insufficient currency')) {
        showAlert(t('Not enough pearls to buy all items'));
      } else {
        showAlert(t('Failed to purchase furniture'));
      }
      await refreshFurnitureCache();
    }
    setPreviewPurchaseLoading(null);
    // createUserAction з useCreateUserAction() — нова fn щорендеру; fire-and-forget analytics.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- createUserAction unstable each render
  }, [previewItems, mapObjectId, refreshFurnitureCache, removePreviewItem, clearPreviewFurniture, closeRadialMenu, queryClient, t]);
  // Intentional TDZ-breaking latest-ref: read only from executeRadialAction (a user-event
  // callback), never during render.
  // eslint-disable-next-line react-hooks/refs -- latest-ref bridge for radial execute (post-commit)
  handlePreviewBuyAllRef.current = handlePreviewBuyAll;

  return {
    previewPurchaseFurniture,
    setPreviewPurchaseFurniture,
    previewPurchaseInstanceId,
    setPreviewPurchaseInstanceId,
    previewPurchaseLoading,
    setPreviewPurchaseLoading,
    handleMirrorToggle,
    handleUnplaceFurniture,
    executeRadialAction,
    refreshFurnitureCache,
    handlePreviewBuy,
    handlePreviewBuyAll,
  };
}
